import { ThunkDispatch } from "core-integration/src/store";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IPick } from "core-integration/src/store/my-team/types";
import { resetLastChange } from "core-integration/src/store/squad/actions";
import {
  canAutocomplete as canAutocompleteSelector,
  canReset as canResetSelector,
  getErrors,
  getLastChange,
  getProposedElements,
  getSavedPicks,
  getSquadMode,
} from "core-integration/src/store/squad/reducers";
import {
  autoComplete,
  removeElement,
  reset as resetSquad,
  restoreElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { dateLocales } from "../../i18n";
import Alert, { AlertWrap } from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import DeadlineBar from "../DeadlineBar";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import DialogManager from "../DialogManager";
import Link from "../Link";
import Pitch, { PitchRow, PitchUnit } from "../Pitch";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import UnsavedChangesDialog from "../UnsavedChangesDialog";
import Scoreboard from "./Scoreboard";
import SquadActionBar from "./SquadActionBar";
import SquadPitchUnit from "./SquadPitchUnit";

const StyledPitchWrapper = styled(Box)`
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.space[2]};
  margin: 0 ${(props) => props.theme.space[2]};
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-direction: row;
  }
`;

const StyledCopy = styled(Copy)`
  margin-bottom: ${(props) => props.theme.space[6]};
  font-size: ${(props) => props.theme.fontSizes[3]};
  text-align: center;
`;

const StyledDialogButton = styled(DialogButtonItem)`
  flex: 1;
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    margin: 0 auto;
  }
`;

const StyledHelpLink = styled(Link)`
  font-weight: bold;
  cursor: pointer;
  text-decoration: underline;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const ScoreBoardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => `${theme.space[4]} 0`};
  padding: ${({ theme }) => `0.2rem ${theme.space[2]} ${theme.space[2]}`};
  border-top: ${({ theme }) => theme.borders[2]};
  border-bottom: ${({ theme }) => theme.borders[2]};
  border-radius: 0;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    border: ${({ theme }) => theme.borders[2]};
    border-radius: ${({ theme }) => theme.radii[2]};
  }
`;

const StyledSquadActionButton = styled(Button)`
  flex: 1 1 100%;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    padding: ${({ theme }) => theme.space[2]};
    font-size: ${({ theme }) => theme.fontSizes[0]};
  }
`;

//TODO: Move IListTableProps somewhere more abstract?
export interface IListTableProps {
  elementType: number;
  key: string;
  positions: number[];
  renderElementDialog: (element: IElement) => void;
  renderElementMenu: (position: number) => void;
}

interface IOwnProps {
  listTable: (listTableProps: IListTableProps) => React.ReactNode;
  scoreboard: string;
  showSidebar: () => void;
  submitDialog: (
    handleHide: () => void,
    allowNavigation: () => void,
  ) => React.ReactNode;
}

type Props = IOwnProps & WithTranslation;

const CreateSquad = ({
  i18n,
  listTable,
  scoreboard,
  showSidebar,
  submitDialog,
  t,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const currencyDivisor = 10;
  const canAutocomplete = useSelector(canAutocompleteSelector);
  const canReset = useSelector(canResetSelector);
  const elementTypesByPosition = useSelector(getElementTypesBySquadPosition);
  const elementsById = useSelector(getElementsById);
  const errors = useSelector(getErrors);
  const lastChange = useSelector(getLastChange);
  const mode = useSelector(getSquadMode);
  const nextEvent = useSelector(getNextEvent);
  const proposedElements = useSelector(getProposedElements);
  const savedPicks = useSelector(getSavedPicks);
  const rules = useSelector(getRules);
  const teamsById = useSelector(getTeamsById);

  const [positionForMenu, setPositionForMenu] = useState<number>(0);
  const [showAutoPickDialog, setShowAutoPickDialog] = useState<boolean>(false);
  const [showResetDialog, setShowResetDialog] = useState<boolean>(false);

  const autopickCookieName = "autoPickDialogSeen";
  const autopickCookieValue = "1";
  const autopickCookieMaxAgeSeconds = 60 * 60 * 24 * 365; // 1 year

  const hasSeenAutoPickDialog = () => {
    try {
      return document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .some(
          (cookie) => cookie === `${autopickCookieName}=${autopickCookieValue}`,
        );
    } catch {
      return false;
    }
  };

  const markAutoPickDialogSeen = () => {
    document.cookie = `${autopickCookieName}=${autopickCookieValue}; path=/; max-age=${autopickCookieMaxAgeSeconds}; samesite=strict; secure`;
  };

  const hasUnsavedChanges = useMemo(() => {
    const positions = new Set();
    Object.keys(savedPicks).forEach((key) => positions.add(key));
    Object.keys(proposedElements).forEach((key) => positions.add(key));

    for (const key of positions) {
      const pos = Number(key);
      const proposed = proposedElements[pos];
      const saved = savedPicks[pos];

      // Check if player added/removed at position
      if (proposed?.id !== saved?.element) {
        return true;
      }
    }

    return false;
  }, [proposedElements, savedPicks]);

  const skipBlockRef = useRef(false);

  // Callback to allow navigation
  // e.g. when confirming transfers in ConfirmTransfersDialog
  const allowNavigation = () => {
    skipBlockRef.current = true;
  };
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (skipBlockRef.current) {
      return false;
    }

    return (
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  const handleShowMenuForElement = (position: number) =>
    setPositionForMenu(position);

  const handleHideMenuForElement = () => setPositionForMenu(0);

  const handleShowAutoPickDialog = () => setShowAutoPickDialog(true);

  const handleHideAutoPickDialog = () => {
    markAutoPickDialogSeen();
    setShowAutoPickDialog(false);
  };

  const autoPick = () => {
    if (!dispatch(autoComplete())) {
      // We should do something :-)
      window.console.log("Failed to autocomplete");
    }
  };

  const handleReset = () => setShowResetDialog(true);
  const handleHideResetDialog = () => setShowResetDialog(false);
  const handleConfirmReset = () => {
    dispatch(resetSquad());
    setShowResetDialog(false);
  };
  const handleRemoveElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(removeElement(position));
  };

  const removeAndFilter = (position: number) => {
    dispatch(removeElement(position));
    handleHideMenuForElement();
    dispatch(
      updateElementTypeControl(elementTypesByPosition[position].thisType.id),
    );
  };

  const handleRestoreElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(restoreElement(position));
  };

  const showElementType = (elementTypeId: number, position: number) => {
    handleRemoveElement(position);
    dispatch(updateElementTypeControl(elementTypeId));
    showSidebar();
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  const renderMenu = () => {
    let element: IElement | undefined;
    let mode: "remove" | "restore" | undefined;
    if (proposedElements[positionForMenu]) {
      element = proposedElements[positionForMenu];
      mode = "remove";
    } else if (savedPicks[positionForMenu]) {
      element = elementsById[savedPicks[positionForMenu].element];
      mode = "restore";
    }
    if (!mode || !element) {
      return null;
    }
    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          <DialogHeading>
            {`${element.first_name} ${element.second_name}`}
          </DialogHeading>
        </Dialog.Header>
        <Dialog.Body>
          <PlayerActions>
            {mode === "remove" ? (
              <>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() => removeAndFilter(positionForMenu)}
                    width={1}
                  >
                    {t("createSquad.removePlayer", "Remove player")}
                  </Button>
                </DialogButtonItem>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() =>
                      showElementType(element!.element_type, positionForMenu)
                    }
                    width={1}
                  >
                    {t("createSquad.replacePlayer", "Select replacement")}
                  </Button>
                </DialogButtonItem>
              </>
            ) : mode === "restore" ? (
              <>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() => handleRestoreElement(positionForMenu)}
                    width={1}
                  >
                    {t("createSquad.restorePlayer", "Restore player")}
                  </Button>
                </DialogButtonItem>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() =>
                      showElementType(element!.element_type, positionForMenu)
                    }
                    width={1}
                  >
                    {t("createSquad.replacePlayer", "Select replacement")}
                  </Button>
                </DialogButtonItem>
              </>
            ) : (
              ""
            )}
            <DialogButtonItem>
              <Button
                variant="primary"
                onClick={() => showDialog(element as IElement)}
                width={1}
              >
                {t("createSquad.viewInformation", "View Information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const renderElementValueForPosition = (pos: number) => {
    const element: IElement = proposedElements[pos];
    const pick: IPick | undefined = savedPicks[pos];
    // Initial element selection (squad selection)
    if (!pick && element) {
      return integerToMoney(element.now_cost, currencyDivisor);
    }
    // Removed element (transfers)
    if (pick && !element) {
      return integerToMoney(pick.selling_price, currencyDivisor);
    }
    // Original element (transfers)
    if (pick && element && pick.element === element.id) {
      return integerToMoney(pick.selling_price, currencyDivisor);
    }
    // Replaced element (transfers)
    if (pick && element && pick.element !== element.id) {
      return integerToMoney(element.now_cost, currencyDivisor);
    }
    return null;
  };

  useEffect(() => {
    dispatch(resetLastChange());
    if (
      Object.keys(proposedElements).length === 0 &&
      !hasSeenAutoPickDialog()
    ) {
      // Mark as seen as soon as we show it so navigation away doesn't re-open it.
      markAutoPickDialogSeen();
      handleShowAutoPickDialog();
    }
  }, [dispatch, proposedElements]);

  let latestAction: React.ReactNode = null;
  if (lastChange.type === "addition") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementAdded"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          added to your squad.
        </Trans>
      </Alert>
    );
  } else if (lastChange.type === "removal") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementRemoved"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          removed from your squad.
        </Trans>
      </Alert>
    );
  }

  // Button enablers
  const canEnter = !Object.keys(errors).length;

  // Helper functions to generate props used by multiple component instances
  const sharedPitchElementProps = (pos: number) => ({
    pos,
    renderElementMenu: handleShowMenuForElement,
    elementValue: renderElementValueForPosition(pos),
    showSidebar,
  });

  const sharedSquadListTableProps = (
    pos: number[],
    elementType: number,
    key: string,
  ) => ({
    elementType,
    key,
    positions: pos,
    renderElementDialog: showDialog,
    renderElementMenu: handleShowMenuForElement,
  });

  const submitButtonText = {
    selection: t("createSquad.enterSquad", "Enter Squad"),
    transfers: t("createSquad.makeTransfers", "Make Transfers"),
  };

  const positionsByElementType: {
    [index: string]: number[];
  } = {
    1: [1, 2],
    2: [3, 4, 5, 6, 7],
    3: [8, 9, 10, 11, 12],
    4: [13, 14, 15],
  };

  return (
    <div>
      {blocker.state === "blocked" && (
        <UnsavedChangesDialog
          handleConfirm={() => blocker.state === "blocked" && blocker.proceed()}
          handleHide={() => blocker.state === "blocked" && blocker.reset()}
        />
      )}
      {showResetDialog && (
        <UnsavedChangesDialog
          handleConfirm={handleConfirmReset}
          handleHide={handleHideResetDialog}
          variant="reset"
        />
      )}
      {rules && (
        <Box mt={3}>
          <Alert type={"info"}>
            {t(
              "createSquad.help",
              "Select a maximum of {{ teamLimit }}  players from a single team",
              { teamLimit: rules.squad_team_limit },
            )}
          </Alert>
        </Box>
      )}
      <ScoreBoardWrapper>
        {nextEvent && (
          <DeadlineBar
            deadlineISO={formatRawAsISO(nextEvent.deadline_time)}
            deadlineLocal={formatRawAsLocalI18n(
              nextEvent.deadline_time,
              dateLocales[i18n.language],
            )}
            headingText={nextEvent.name}
          />
        )}
        <Scoreboard scoreboard={scoreboard} />
      </ScoreBoardWrapper>
      {errors.overTeamLimit && (
        <AlertWrap>
          <Alert type="error">
            {t(
              "createSquad.errors.overTeamLimit",
              "Too many players selected from",
            )}{" "}
            <strong>
              {errors.overTeamLimit
                .map((team) => teamsById[team].name)
                .join(", ")}
            </strong>
          </Alert>
        </AlertWrap>
      )}
      {latestAction && <AlertWrap>{latestAction}</AlertWrap>}

      <StyledPitchWrapper pt={4}>
        <Box mx={2} mb={2}>
          <Tabs centered>
            <TabPanel
              label={t("createSquad.pitchView", "Pitch View")}
              link="pitch"
            >
              <Box mx={-2} pt={4}>
                <Pitch>
                  <PitchRow>
                    <PitchUnit />
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(1)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(2)} />
                    </PitchUnit>
                    <PitchUnit />
                  </PitchRow>
                  <PitchRow>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(3)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(4)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(5)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(6)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(7)} />
                    </PitchUnit>
                  </PitchRow>
                  <PitchRow>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(8)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(9)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(10)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(11)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(12)} />
                    </PitchUnit>
                  </PitchRow>
                  <PitchRow>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(13)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(14)} />
                    </PitchUnit>
                    <PitchUnit>
                      <SquadPitchUnit {...sharedPitchElementProps(15)} />
                    </PitchUnit>
                  </PitchRow>
                </Pitch>
              </Box>
            </TabPanel>
            <TabPanel
              label={t("createSquad.listView", "List View")}
              link="pitch"
            >
              <Box mt={4} mb={4} mx="-1px">
                {Object.keys(positionsByElementType).map((et) =>
                  listTable(
                    sharedSquadListTableProps(
                      positionsByElementType[et],
                      parseInt(et, 10),
                      et,
                    ),
                  ),
                )}
              </Box>
            </TabPanel>
          </Tabs>
        </Box>
      </StyledPitchWrapper>
      <DialogManager
        render={(showDialog, handleShow, handleHide) => (
          <>
            <SquadActionBar>
              <StyledSquadActionButton
                variant="secondary"
                onClick={autoPick}
                disabled={!canAutocomplete}
              >
                {t("autoPick", "Auto Pick")}
              </StyledSquadActionButton>
              <StyledSquadActionButton
                variant="secondary"
                onClick={handleReset}
                disabled={!canReset}
              >
                {t("reset", "Reset")}
              </StyledSquadActionButton>
              <StyledSquadActionButton
                variant="primary"
                onClick={handleShow}
                disabled={!canEnter}
                width={1}
              >
                {submitButtonText[mode]}
              </StyledSquadActionButton>
            </SquadActionBar>

            {showDialog && submitDialog(handleHide, allowNavigation)}
          </>
        )}
      />
      {renderMenu()}
      {showAutoPickDialog && (
        <Dialog closeDialog={handleHideAutoPickDialog}>
          <Dialog.Header closeDialog={handleHideAutoPickDialog}>
            <DialogHeading>
              {t("createSquad.onboarding.title", "Squad Selection")}
            </DialogHeading>
          </Dialog.Header>
          <Dialog.Body>
            <StyledCopy>
              <p>
                {t("createSquad.onboarding.welcomeTo", "Welcome to")}{" "}
                <BoldText>
                  {t("createSquad.onboarding.rslFantasy", "RSL Fantasy!")}
                </BoldText>{" "}
                {t(
                  "createSquad.onboarding.timeToPickSquad",
                  "It's time to pick your Squad.",
                )}
              </p>
              <p>
                {t("createSquad.onboarding.hit", "Hit")}{" "}
                <BoldText>
                  {t("createSquad.onboarding.autoPick", "Auto Pick")}
                </BoldText>{" "}
                {t(
                  "createSquad.onboarding.toInstantlyAssemble",
                  "to instantly assemble your squad, or dive in and craft your dream team player by player. You can select up to",
                )}{" "}
                <BoldText>
                  {t(
                    "createSquad.onboarding.threePlayersFromSameClub",
                    "3 players from the same RSL club.",
                  )}
                </BoldText>
              </p>
              <p>
                {t(
                  "createSquad.onboarding.youAreFree",
                  "You're free to tweak and fine-tune your team",
                )}{" "}
                <BoldText>
                  {t(
                    "createSquad.onboarding.anyTimeBeforeDeadline",
                    "any time before your first Gameweek deadline",
                  )}{" "}
                </BoldText>
                {t("createSquad.onboarding.andYes", "and yes,")}{" "}
                <BoldText>
                  {t(
                    "createSquad.onboarding.allTransfersFree",
                    "all transfers are FREE until then!",
                  )}
                </BoldText>
              </p>
              <p>
                <BoldText>{t("createSquad.onboarding.tip", "Tip:")}</BoldText>{" "}
                {t(
                  "createSquad.onboarding.startWithGoToStars",
                  "Start with your go-to stars or club favorites, then build a winning strategy around them.",
                )}
              </p>
              <p>
                <StyledHelpLink to="/help">
                  {t(
                    "createSquad.onboarding.helpPageLink",
                    "Head to our Help page",
                  )}
                </StyledHelpLink>{" "}
                {t(
                  "createSquad.onboarding.p5n",
                  "to learn how to dominate the league.",
                )}
              </p>
            </StyledCopy>
            <ButtonWrap>
              <StyledDialogButton>
                <Button
                  onClick={() => {
                    autoPick();
                    handleHideAutoPickDialog();
                  }}
                  width={1}
                  variant="primary"
                >
                  {t("createSquad.onboarding.autoPickn", "Auto Pick")}
                </Button>
              </StyledDialogButton>
              <StyledDialogButton>
                <Button
                  onClick={handleHideAutoPickDialog}
                  width={1}
                  variant="tertiary"
                >
                  {t(
                    "createSquad.onboarding.pickMySquad",
                    "I'll pick my squad!",
                  )}
                </Button>
              </StyledDialogButton>
            </ButtonWrap>
          </Dialog.Body>
        </Dialog>
      )}
    </div>
  );
};

export default withTranslation()(CreateSquad);
